import { useContext, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import aspectRatio from 'calculate-aspect-ratio';
import { store } from '../store';
import { SET_ALLOW_DRAG, SET_IMAGE_SCALE } from '../types';
import {
  getLayout,
  getSuggestedDimension,
  getElementDimensions,
  getFitAction,
} from '../utils';
import useDragWithin from '../hooks/useDragWithin';
import ImageUploadButton from './ImageUploadButton';

const Photo = ({ cols, rows }) => {
  const {
    paperLayout,
    image,
    border,
    stretchImage,
    grayscale,
    pickingColor,
    scale,
    allowDrag,
    dispatch,
  } = useContext(store);
  const [photoLayout, setPhotoLayout] = useState();
  const [photoWidth, setPhotoWidth] = useState();
  const [photoHeight, setPhotoHeight] = useState();
  const containerRef = useRef();
  const photoRef = useRef();
  const dragOptions = { x: '50%', y: '50%' };
  const [position, handleMouseDown, overridePosition] = useDragWithin(
    photoRef,
    containerRef,
    dragOptions,
    allowDrag
  );

  const reset = () => {
    dispatch({ type: SET_ALLOW_DRAG, payload: false });
    dispatch({ type: SET_IMAGE_SCALE, payload: 1 });
    overridePosition(dragOptions);
  };

  const setLayout = (event) => {
    const { target: media } = event;
    const { naturalWidth: width, naturalHeight: height } = media;
    const ratio = aspectRatio(width, height);
    const layout = getLayout(ratio);
    setPhotoLayout(layout);
    reset();
  };

  useEffect(() => {
    const fitAction = getFitAction(paperLayout, photoLayout, stretchImage);
    const fitWidth = () => {
      setPhotoWidth('100%');
      setPhotoHeight();
    };
    const fitHeight = () => {
      setPhotoWidth();
      setPhotoHeight('100%');
    };
    if (fitAction === 'fitHeight') {
      fitHeight();
    } else {
      fitWidth();
    }
    reset();
  }, [paperLayout, photoLayout, stretchImage]);

  useEffect(() => {
    const { width: cWidth, height: cHeight } = getElementDimensions(
      containerRef
    );
    const { width: imageWidth, height: imageHeight } = getElementDimensions(
      photoRef
    );
    if (cWidth && cHeight && imageWidth && imageHeight) {
      const fitAction = getFitAction(paperLayout, photoLayout, stretchImage);
      const fixedDimension =
        fitAction === 'fitHeight' ? { height: cHeight } : { width: cWidth };

      const { width, height } = getSuggestedDimension(
        { width: imageWidth, height: imageHeight },
        fixedDimension
      );

      setPhotoWidth(width * scale);
      setPhotoHeight(height * scale);
    }
  }, [scale]);

  useEffect(() => {
    reset();
  }, [image]);

  return image ? (
    <PhotoContainer
      ref={containerRef}
      hasBorder={border}
      cols={cols}
      rows={rows}
    >
      <Image
        ref={photoRef}
        id="photo"
        onLoad={setLayout}
        onMouseDown={handleMouseDown}
        src={image}
        grayscale={grayscale}
        stretchImage={stretchImage}
        allowDrag={allowDrag}
        paperLayout={paperLayout}
        layout={photoLayout}
        width={photoWidth}
        height={photoHeight}
        top={position.y}
        left={position.x}
        draggable={false}
        scale={scale}
        pickingColor={pickingColor}
      />
    </PhotoContainer>
  ) : (
    <ButtonContainer colStart={cols / 2} rowStart={rows / 2}>
      <ImageUploadButton size="massive" />
    </ButtonContainer>
  );
};

const PhotoContainer = styled.div(({ hasBorder, cols, rows }) => ({
  position: 'relative',
  gridColumnStart: hasBorder ? 2 : 1,
  gridColumnEnd: hasBorder ? cols : cols + 1,
  gridRowStart: hasBorder ? 2 : 1,
  gridRowEnd: hasBorder ? rows : rows + 1,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
}));

const ButtonContainer = styled.div(
  {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  ({ colStart, rowStart }) => ({
    gridColumnStart: colStart,
    gridRowStart: rowStart,
  })
);

const Image = styled.img(
  ({ width, height, stretchImage, allowDrag, grayscale, top, left }) => ({
    display: 'block',
    position: 'absolute',
    width,
    height,
    top: !stretchImage ? '50%' : top,
    left: !stretchImage ? '50%' : left,
    transform: !stretchImage ? 'translate(-50%, -50%)' : undefined,
    filter: grayscale ? 'grayscale(100%)' : null,
    cursor: allowDrag ? 'move' : 'default',
  }),
  ({ pickingColor }) => ({
    cursor: pickingColor ? 'copy' : undefined,
  })
);

export default Photo;
